import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { PageHeader, SectionHeader } from "../components/Section";
import ClientsMarquee from "../components/ClientsMarquee";

const TEAM_IMG =
  "https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWxzJTIwbWVldGluZyUyMGJvYXJkcm9vbXxlbnwwfHx8fDE3Nzc0MzA2NDd8MA&ixlib=rb-4.1.0&q=85";

const PRINCIPLES = [
  {
    no: "01",
    title: "Practitioners, not lecturers",
    body: "Every senior consultant has shipped projects that moved P&L lines — banks, hospitals, factories, retail networks. We teach what we've done.",
  },
  {
    no: "02",
    title: "Discipline before sophistication",
    body: "Most clients don't need fancier tools — they need the basics, applied with rigour, every day. That is where we start.",
  },
  {
    no: "03",
    title: "Asia-Pacific by design",
    body: "From Hong Kong, with delivery teams across Singapore, Australia and Greater China. Local context shapes how we sequence the work.",
  },
  {
    no: "04",
    title: "Benefits, tracked",
    body: "If it doesn't show up in the management accounts, it didn't happen. Our governance ensures it does.",
  },
];

const DMAIC = [
  { phase: "Define", body: "Charter, problem statement, scope, voice of customer, value at stake." },
  { phase: "Measure", body: "Operational definitions, MSA, baseline capability, sigma level." },
  { phase: "Analyse", body: "Process & data analysis, hypothesis testing, root-cause validation." },
  { phase: "Improve", body: "Solution design, DOE, pilot, change-impact assessment." },
  { phase: "Control", body: "Control plan, SPC, leader-standard work, project handover." },
];

const About = () => (
  <div data-testid="page-about">
    <PageHeader
      overline="About"
      title={<>Precision in process. <span className="text-[#E63946]">Excellence in execution.</span></>}
      kicker="OpExcellence is a Hong Kong–headquartered training and consulting firm focused on Lean Six Sigma, operational excellence and business turnaround across the Asia-Pacific."
    />

    <section className="border-b border-black/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <img
            src={TEAM_IMG}
            alt="OpExcellence consulting team in a strategic working session"
            className="w-full h-auto object-cover border border-black/10"
            data-testid="about-team-image"
          />
        </div>
        <div className="lg:col-span-7">
          <p className="text-xs tracking-[0.22em] uppercase text-[#E63946] font-semibold mb-6">
            Our story
          </p>
          <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter leading-[1.05] text-black">
            Built where the work happens. Trusted where the decisions are made.
          </h2>
          <div className="mt-8 space-y-5 text-zinc-700 leading-relaxed">
            <p>
              We started with a simple observation: too many improvement programmes in the
              Asia-Pacific deliver impressive slideware and disappointing P&Ls. The tools were
              right; the discipline of execution was missing.
            </p>
            <p>
              For two decades, our team has worked alongside operators in banks, hospitals,
              construction, retail and manufacturing — designing operating systems that make
              excellence the default, not the exception.
            </p>
            <p>
              Today OpExcellence.hk delivers Lean Six Sigma certification (Yellow, Green and
              Black Belt), Lean foundational and leadership programmes, and consulting
              engagements ranging from full business turnarounds to operating-system design.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="about-contact-cta"
            className="group mt-10 inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-sm font-semibold hover:bg-[#E63946] transition-colors"
          >
            Talk to a partner <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>

    <section className="border-b border-black/10 bg-[#F7F7F8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <SectionHeader
          overline="Principles"
          title={<>Four principles that shape <span className="text-[#E63946]">every engagement.</span></>}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
          {PRINCIPLES.map((p) => (
            <div key={p.no} className="bg-white p-10 lg:p-12" data-testid={`principle-${p.no}`}>
              <div className="flex items-start justify-between mb-6">
                <span className="font-mono-tech text-xs tracking-widest text-zinc-500">
                  {p.no} / 04
                </span>
                <span className="w-1.5 h-1.5 bg-[#E63946] mt-2" />
              </div>
              <h3 className="font-display text-2xl font-bold tracking-tight text-black">
                {p.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-zinc-600">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-black/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.22em] uppercase text-[#E63946] font-semibold mb-6">
              Methodology
            </p>
            <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter leading-[1.05]">
              The DMAIC backbone.
            </h2>
            <p className="mt-6 text-zinc-600 leading-relaxed">
              Define, Measure, Analyse, Improve, Control — the international standard for
              structured improvement, applied to every project we run and every belt we
              certify.
            </p>
          </div>
          <div className="lg:col-span-8">
            <ol className="border-t border-black/10">
              {DMAIC.map((d, i) => (
                <li
                  key={d.phase}
                  data-testid={`dmaic-${d.phase.toLowerCase()}`}
                  className="grid grid-cols-12 gap-6 py-8 border-b border-black/10"
                >
                  <div className="col-span-2 font-mono-tech text-xs tracking-widest text-zinc-500">
                    0{i + 1}
                  </div>
                  <div className="col-span-3">
                    <h4 className="font-display text-xl font-bold text-black">{d.phase}</h4>
                  </div>
                  <div className="col-span-7 text-sm text-zinc-600 leading-relaxed">
                    {d.body}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>

    <ClientsMarquee heading="A few of the institutions we've worked alongside" />

    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 text-center">
        <p className="text-xs tracking-[0.22em] uppercase text-[#E63946] font-semibold mb-6">
          Next
        </p>
        <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tighter leading-[1.05] max-w-3xl mx-auto">
          Let's discuss your operational ambition.
        </h2>
        <Link
          to="/contact"
          data-testid="about-bottom-cta"
          className="group mt-10 inline-flex items-center gap-2 bg-[#E63946] text-white px-7 py-4 text-sm font-semibold hover:bg-[#D32F2F] transition-colors"
        >
          Start a conversation <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  </div>
);

export default About;
